import { Avatar, Box, Flex, Select, Text } from "@chakra-ui/react";
import React from "react";

import { IconButton } from "../../../components";
import SharedWithAvatar from "../ShareModal/SharedWithAvatar";
import { ShareUser } from "./types";

export interface UserAccessRowProps<TRole extends string = string> {
  avatarName?: string;
  avatarSrc?: string | null;
  avatarBg?: string;
  avatarIcon?: React.ReactNode;
  user?: ShareUser; // When provided, render using SharedWithAvatar
  primaryText: string;
  secondaryText?: string;
  // Layout mode: 'edit' (modal) vs 'readonly' (static list)
  layout?: "edit" | "readonly";
  // Editable role control (select)
  roleValue?: TRole;
  availableRoles?: Array<{ value: TRole; label: string }>;
  onChangeRole?: (role: TRole) => void;
  // Static role display (text)
  roleLabel?: string;
  // Optional remove action
  onRemove?: () => void | Promise<void>;
  // Test id hook
  testId?: string;
}

export function UserAccessRow<TRole extends string = string>({
  avatarName,
  avatarSrc,
  avatarBg,
  avatarIcon,
  user,
  primaryText,
  secondaryText,
  layout = "edit",
  roleValue,
  availableRoles,
  onChangeRole,
  roleLabel,
  onRemove,
  testId,
}: UserAccessRowProps<TRole>): React.ReactElement {
  const isEditableRole =
    typeof roleValue !== "undefined" &&
    Array.isArray(availableRoles) &&
    !!availableRoles.length &&
    typeof onChangeRole === "function";
  const isReadonly = layout === "readonly";

  return (
    <Flex
      align="center"
      py="8px"
      width="100%"
      data-testid={testId}
      justifyContent={isReadonly ? "space-between" : "flex-start"}
    >
      {user ? (
        <SharedWithAvatar user={user} size="sm" w="32px" h="32px" mr="12px" />
      ) : (
        <Avatar
          size="sm"
          w="32px"
          h="32px"
          name={avatarName}
          src={avatarSrc || undefined}
          mr="12px"
          bg={avatarBg}
          icon={avatarIcon as any}
        />
      )}
      <Box flex={1}>
        <Text
          fontSize="14px"
          fontWeight="400"
          lineHeight="20px"
          color="gray.800"
        >
          {primaryText}
        </Text>
        {secondaryText ? (
          <Text fontSize="12px" lineHeight="16px" color="gray.500">
            {secondaryText}
          </Text>
        ) : null}
      </Box>

      <Box
        width="96px"
        minW="96px"
        maxW="96px"
        display="flex"
        alignItems="center"
        justifyContent="flex-start"
        ml={isReadonly ? "auto" : undefined}
        mr={isReadonly ? (onRemove ? "8px" : 0) : "8px"}
      >
        {isEditableRole ? (
          <Select
            size="sm"
            h="40px"
            value={roleValue as any}
            onChange={(e) => onChangeRole?.(e.target.value as TRole)}
            width="96px"
            minW="96px"
            maxW="96px"
            fontSize="14px"
            borderColor="gray.200"
            borderRadius="6px"
            _hover={{ borderColor: "gray.400" }}
            _focus={{
              borderColor: "blue.500",
              boxShadow: "0 0 0 1px #3182CE",
            }}
          >
            {availableRoles.map((role) => (
              <option key={role.value as any} value={role.value as any}>
                {role.label}
              </option>
            ))}
          </Select>
        ) : (
          <Text
            fontSize="14px"
            lineHeight="20px"
            color="gray.600"
            width="96px"
            paddingLeft="3"
            textAlign={isReadonly ? "right" : "left"}
          >
            {roleLabel}
          </Text>
        )}
      </Box>

      {onRemove ? (
        <Box
          w="40px"
          h="40px"
          p="8px"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <IconButton
            aria-label="Remove access"
            icon={
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <line
                  x1="2"
                  y1="2"
                  x2="14"
                  y2="14"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
                <line
                  x1="14"
                  y1="2"
                  x2="2"
                  y2="14"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            }
            size="sm"
            variant="ghost"
            color="gray.500"
            minW="32px"
            h="32px"
            w="32px"
            _hover={{ color: "gray.700", bg: "gray.50" }}
            onClick={() => onRemove()}
            p={0}
          />
        </Box>
      ) : layout === "edit" ? (
        <Box w="40px" h="40px" />
      ) : null}
    </Flex>
  );
}

export default UserAccessRow;
